import { cn } from "@/lib/utils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { Project, ProjectWithStatuses } from "@/utils/types";
import {
  format,
  formatDistanceStrict,
  isWithinInterval,
  subDays,
  startOfDay,
  isPast,
  addDays,
  isSameDay,
} from "date-fns";
import {
  PlusIcon,
  Loader2,
  UserPlus,
  Calendar,
  Tag,
  BarChart,
  CircleDashed,
  UserCircle,
  Minus,
  User,
  Circle,
  TriangleDashed,
} from "lucide-react";
import * as React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSearchParams } from "react-router";
import { useCreateCanvasModal } from "@/hooks/useCreateCanvasModal";
import CreateCanvasModal from "../CreateCanvasModal";
import { FilterState, StatusColumn } from "./types";
import { TEMPLATE_PROJECT_PATHS } from "@/utils/templateProjects";
import NoRepositoryModal from "../NoRepositoryModal";
import CreateRepositoryModal from "../CreateRepositoryModal";
import { GitProvider } from "@/utils/gitProviderUtils";
import { ADMIN_USER_IDS, useUserId } from "@/utils/utils.tsx";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { IssueGrid } from "@/components/common/issues/issue-grid";
import { CirclePartialFillIcon } from "@/assets/icons/circle-partial-fill-icon";
import { CircleFillCheckmarkIcon } from "@/assets/icons/circle-fill-checkmark-icon";
import { CircleFillXIcon } from "@/assets/icons/circle-fill-x-icon";
import { DraggableIssueCard } from "@/components/common/issues/issue-card";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFocusStore } from "@/store/zustand/focusStore";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useIssueKeyboardShortcuts } from "@/hooks/useIssueKeyboardShortcuts";
import { toast } from "sonner";
import {
  CommandPaletteMode,
  CommandPaletteOption,
  CommandPaletteSubMenuItem,
} from "@/store/zustand/commandPaletteStore";
import { OptionCategory, useCommandPalette } from "@/hooks/useCommandPalette";
import HalfFilledTriangleIcon from "@/assets/icons/half-filled-triangle-icon";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { defaultPriorities } from "@/types/priorities";
import {
  ESTIMATE_POINTS,
  FeaturePRD,
  getTShirtSize,
  Issue,
} from "@/types/issues";
import { LabelInterface } from "@/types/labels";
import { useShortcuts } from "../providers/ShortcutProvider";
import { getModifierKey } from "@/utils/utils";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { useDatabaseSyncWrapperStore } from "../contexts/databaseSyncStore";
import { Button } from "../ui/button";
import { IssueContextMenu } from "@/components/common/issues/issue-context-menu";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { useContextMenuStore } from "@/store/zustand/contextMenuStore";
import { UserAvatar } from "../common/UserAvatar";
import NoResults from "./NoResults";
import { useCanvasStatusManagement } from "@/components/ProjectConfiguration/hooks";
import { validateCustomStatus } from "@/components/ProjectConfiguration/utils/validateCustomStatus";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import useLayoutStore from "@/store/zustand/layoutStore";
import { Status } from "@/types/status";
import {
  STATE_KEY_TO_STATUS_ID as SETTINGS_STATE_KEY_TO_STATUS_ID,
  STATUS_TYPE_MAPPING,
} from "@/components/ProjectConfiguration/constants";
import { getColorValue } from "@/utils/labelUtils";
import { useStatusIconStore } from "@/store/zustand/statusIconStore";
import { resolveIssueStatus } from "@/utils/filterUtils";
import { getColumnConfig } from "@/components/ProjectConfiguration/utils/columnConfigUtils";
import {
  isDefaultStatusName as isDefaultStatusNameUtil,
  getStatusNameFromStateKey as getStatusNameFromStateKeyUtil,
} from "@/components/ProjectConfiguration/utils/statusNameUtils";

const STATE_KEY_TO_STATUS_ID: Record<string, string> =
  SETTINGS_STATE_KEY_TO_STATUS_ID;
import { Canvas } from "../AI/PRD/constants";
import NoIssues from "./NoIssues";
import {
  FilterCondition,
  FilterConditionConsolidated,
} from "@/types/filterEnums";
// TODO: Use local status icon definitions to avoid React version conflicts
// import { status as STATUS_LIST } from '@/mock-data/status';

export const STATUS_LIST = [
  {
    id: "backlog",
    name: "Backlog",
    color: "#ec4899",
    icon: () => <CircleDashed className="size-4 pl-[3px]" color="#F5F5F6" />,
    headerIcon: () => (
      <CircleDashed className="size-4 pl-[3px]" color="#F5F5F6" />
    ),
    badgeIcon: () => (
      <CircleDashed className="w-3.5 h-3.5 mt-[3px]" color="#F5F5F6" />
    ),
    filterIcon: () => <CircleDashed className="size-3.5" color="#F5F5F6" />,
  },
  {
    id: "todo",
    name: "Todo",
    color: "#f97316",
    icon: () => <Circle className="size-4 pl-[3px]" color="#F5F5F6" />,
    headerIcon: () => <Circle className="size-4 pl-[3px]" color="#F5F5F6" />,
    badgeIcon: () => (
      <Circle className="w-3.5 h-3.5 mt-[3px]" color="#F5F5F6" />
    ),
    filterIcon: () => <Circle className="size-3.5" color="#F5F5F6" />,
  },
  {
    id: "in-progress",
    name: "In Progress",
    color: "#facc15",
    icon: () => <CirclePartialFillIcon color="#CA8A04" />,
    headerIcon: () => <CirclePartialFillIcon color="#CA8A04" />,
    filterIcon: () => (
      <CirclePartialFillIcon color="#CA8A04" className="size-5" />
    ),
  },
  {
    id: "in-review",
    name: "In Review",
    color: "#f97316",
    icon: () => (
      <CirclePartialFillIcon color="#16A34A" startAngle={270} endAngle={90} />
    ),
    headerIcon: () => (
      <CirclePartialFillIcon color="#16A34A" startAngle={270} endAngle={90} />
    ),
    filterIcon: () => (
      <CirclePartialFillIcon
        color="#16A34A"
        startAngle={270}
        endAngle={90}
        className="size-5"
      />
    ),
  },
  {
    id: "blocked",
    name: "Blocked",
    color: "#f97316",
    icon: () => (
      <CirclePartialFillIcon color="#DC2626" startAngle={180} endAngle={90} />
    ),
    headerIcon: () => (
      <CirclePartialFillIcon color="#DC2626" startAngle={180} endAngle={90} />
    ),
    filterIcon: () => (
      <CirclePartialFillIcon
        color="#DC2626"
        startAngle={180}
        endAngle={90}
        className="size-5"
      />
    ),
  },
  {
    id: "done",
    name: "Done",
    color: "#22c55e",
    icon: () => <CircleFillCheckmarkIcon color="#6448EF" />,
    headerIcon: () => <CircleFillCheckmarkIcon color="#6448EF" />,
    filterIcon: () => (
      <CircleFillCheckmarkIcon color="#6448EF" className="size-5" />
    ),
  },
  {
    id: "canceled",
    name: "Canceled",
    color: "#f97316",
    icon: () => <CircleFillXIcon circleColor="#606064" />,
    headerIcon: () => <CircleFillXIcon circleColor="#606064" />,
    filterIcon: () => (
      <CircleFillXIcon circleColor="#606064" className="size-5" />
    ),
  },
  {
    id: "paused",
    name: "Paused",
    color: "#0ea5e9",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-blue-400" />
    ),
  },
  {
    id: "technical-review",
    name: "Technical Review",
    color: "#22c55e",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-green-400" />
    ),
  },
  {
    id: "completed",
    name: "Completed",
    color: "#8b5cf6",
    icon: () => (
      <span className="inline-block w-3 h-3 rounded-full bg-purple-400" />
    ),
  },
];

export enum KanbanDefaultStates {
  BACKLOG = "Backlog",
  TODO = "Todo",
  IN_PROGRESS = "In Progress",
  IN_REVIEW = "In Review",
  BLOCKED = "Blocked",
  DONE = "Done",
  CANCELED = "Canceled",
}

// Filter STATUS_LIST to only include statuses used in columns
export const columnStatuses = STATUS_LIST.filter((status) =>
  Object.values(STATE_KEY_TO_STATUS_ID).includes(status.id),
);

interface ColumnProps {
  stateKey: string;
  title: string;
  issues: Issue[];
  onSelect: (canvas: Canvas) => void;
  project: Project | null;
  onClickAdd: (stateKey: string) => void;
  issueRefs: { [key: string]: HTMLDivElement };
  visibleIssueIds: string[];
  comprehensiveStatusList: Status[];
  customProjectStatuses: ProjectStatus[];
  isLoading: boolean;
  isDragging: boolean;
  statusIconMap: Map<string, (() => JSX.Element) | undefined>;
  issueStatusIconMap: Map<string, (() => JSX.Element) | undefined>;
}

const KanbanColumn: React.FC<ColumnProps> = React.memo(
  ({
    stateKey,
    title,
    issues,
    onSelect,
    project,
    onClickAdd,
    issueRefs,
    visibleIssueIds,
    comprehensiveStatusList,
    customProjectStatuses,
    isLoading,
    isDragging,
    statusIconMap,
    issueStatusIconMap,
  }) => {
    const StatusIcon = useMemo(() => {
      let StatusIcon: (() => JSX.Element) | undefined;

      // Try to get icon by stateKey first (could be enum key like "CANCELED" or status name)
      StatusIcon = statusIconMap.get(stateKey);

      if (!StatusIcon) {
        // Try to find by status name (title)
        StatusIcon = statusIconMap.get(title);
      }

      if (!StatusIcon) {
        // Check if it's a custom status
        const customStatus = customProjectStatuses.find(
          (status) => status.id === stateKey || status.name === title,
        );
        if (customStatus) {
          StatusIcon = statusIconMap.get(customStatus.name);
        }
      }

      // Fallback to backlog if still not found
      if (!StatusIcon) {
        StatusIcon = statusIconMap.get(KanbanDefaultStates.BACKLOG);
      }

      return StatusIcon;
    }, [stateKey, title, customProjectStatuses, statusIconMap]);

    const issueIcons = useMemo(() => {
      const iconMap = new Map<string, (() => JSX.Element) | undefined>();
      issues.forEach((issue) => {
        const icon = issueStatusIconMap.get(issue.canvas.id);
        if (icon) {
          iconMap.set(issue.canvas.id, icon);
        }
      });
      return iconMap;
    }, [issues, issueStatusIconMap]);

    const StatusHeaderIcon = StatusIcon;

    if (!project || isLoading) {
      // render skeleton
      return (
        <div className="flex flex-col basis-90 gap-4 shrink-0 pt-3.5 p-2">
          <div className="flex flex-row gap-1 items-center">
            {/* Status Icon on the left */}
            {StatusHeaderIcon ? <StatusHeaderIcon /> : null}
            {/* Title and count badge */}
            <span className="caption text-2xs font-medium flex items-center min-w-0">
              <span className="truncate max-w-[120px]" title={title}>
                {title}
              </span>
            </span>
            <span className="px-1 rounded-xs text-2xs font-medium bg-secondary-n-900 text-center flex items-center justify-center">
              {issues.length}
            </span>
          </div>
          <div className="flex flex-col gap-2 max-w-full grow pb-8 group rounded-md overflow-y-scroll min-h-0">
            {new Array(3).fill(0).map((_, i) => (
              <div
                key={i}
                className="w-full col-span-1 bg-secondary-n-950 rounded-lg p-3 flex flex-col min-h-28 justify-between"
                style={{
                  animation: "pulse 3s ease-in-out infinite",
                  animationDelay: `${i * 0.2}s`,
                }}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center justify-between">
                    <div className="h-4 w-24 bg-secondary-n-800 rounded" />
                    <div className="h-6 w-6 bg-secondary-n-800 rounded-full" />
                  </div>
                  <div className="grow min-h-8">
                    <div className="h-4 w-3/4 bg-secondary-n-800 rounded" />
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between gap-1">
                  <div className="h-4 w-16 bg-secondary-n-800 rounded" />
                  <div className="h-4 w-20 bg-secondary-n-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div
        className="flex flex-col basis-90 min-h-[calc(100vh-120px)] shrink-0 bg-sidebar/50 rounded-xl overflow-hidden shadow-xs"
        data-status-column={title}
      >
        <div
          className={`text-xs font-normal p-2 tracking-wide flex items-center shrink-0`}
        >
          <div className="flex flex-row gap-1 items-center">
            {/* Status Icon on the left */}
            {StatusHeaderIcon ? <StatusHeaderIcon /> : null}
            {/* Title and count badge */}
            <span className="caption text-2xs font-medium flex items-center min-w-0">
              <span className="truncate max-w-[200px]" title={title}>
                {title}
              </span>
            </span>
            <span className="px-1 rounded-xs text-2xs font-medium bg-secondary-n-900 text-center flex items-center justify-center">
              {issues.length}
            </span>
          </div>
          {/* Group the action buttons together */}
          <div className="ml-auto flex items-center gap-1">
            {/* Add Feature button */}
            <Button
              onClick={() => onClickAdd(stateKey)}
              className="w-7 h-7 rounded-md"
              aria-label="Add Feature"
              type="button"
              variant="ghost"
              size="icon"
            >
              <PlusIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Droppable droppableId={stateKey} ignoreContainerClipping={true}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn(
                "flex flex-col gap-1 max-w-full grow overflow-y-auto p-2 group",
                snapshot.isDraggingOver && "bg-background-secondary",
              )}
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgb(82 82 91) transparent",
                height: "calc(100vh - 180px)",
              }}
            >
              <div
                className={cn("flex flex-col gap-2", {
                  hidden: !issues.length,
                })}
              >
                {issues.map((issue, index) => {
                  if (!issue) return null; // Skip if issue is undefined

                  // Ensure we have a valid date string before creating Date object
                  const lastUpdated = issue?.canvas?.last_updated;
                  const updatedAt = lastUpdated ? new Date(lastUpdated) : null;
                  const now = new Date();
                  const threeDaysAgo = subDays(now, 5);

                  let dateString: string | null = null;

                  if (updatedAt && !isNaN(updatedAt.getTime())) {
                    if (
                      isWithinInterval(updatedAt, {
                        start: threeDaysAgo,
                        end: now,
                      })
                    ) {
                      dateString = formatDistanceStrict(updatedAt, now, {
                        addSuffix: true,
                      });
                    } else {
                      dateString = format(updatedAt, "PPP");
                    }
                  }

                  return (
                    <Draggable
                      draggableId={issue.canvas.id}
                      index={index}
                      key={issue.canvas.id}
                    >
                      {(provided, snapshot) => (
                        <div>
                          <IssueGrid
                            icon={issueIcons.get(issue.canvas.id)}
                            issue={issue}
                            users={
                              Array.isArray(project?.users) ? project.users : []
                            }
                            labels={
                              Array.isArray(project?.labels)
                                ? project.labels
                                : []
                            }
                            statuses={comprehensiveStatusList}
                            onClick={() => onSelect(issue.canvas)}
                            provided={provided}
                            snapshot={snapshot}
                            issueRefs={issueRefs}
                            visibleIssueIds={visibleIssueIds}
                            project={project}
                            isDragging={isDragging}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              </div>
              <Button
                onClick={() => onClickAdd(stateKey)}
                aria-label="Add Canvas"
                data-testid="add-canvas-button"
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  "hover:bg-secondary-n-950 shrink-0 transition-colors ring-1 ring-inset ring-secondary-n-800",
                  isDragging
                    ? "opacity-0"
                    : "opacity-0 group-hover:opacity-100",
                )}
              >
                <PlusIcon className="w-4 h-4" />
              </Button>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  },
);

interface Props {
  project: ProjectWithStatuses | null;
  onSelect: (canvas: Canvas) => void;
  filters: FilterState | undefined;
  viewType: "grid" | "list";
  clearFilters: () => void;
}

const CanvasKanban: React.FC<Props> = ({
  onSelect,
  project,
  filters,
  viewType,
  clearFilters,
}) => {
  const userId = useUserId();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const currentProject = useProjectWithUsers();
  const currentProjectIssues = useIssuesStore(
    (store) => store.issuesByProject[currentProject?.id ?? ""],
  );

  const fetchIssuesForProject = useIssuesStore(
    (state) => state.fetchIssuesForProject,
  );
  const isProjectStatusProcessingComplete = useIssuesStore(
    (state) => state.isProjectStatusProcessingComplete,
  );
  const bulkUpdateIssueAssignee = useIssuesStore(
    (state) => state.bulkUpdateIssueAssignee,
  );
  const bulkUpdateIssuePriority = useIssuesStore(
    (state) => state.bulkUpdateIssuePriority,
  );
  const bulkUpdateIssueStatus = useIssuesStore(
    (state) => state.bulkUpdateIssueStatus,
  );
  const bulkUpdateIssueEstimate = useIssuesStore(
    (state) => state.bulkUpdateIssueEstimate,
  );
  const bulkUpdateIssueLabel = useIssuesStore(
    (state) => state.bulkUpdateIssueLabel,
  );
  const bulkUpdateIssueDueDate = useIssuesStore(
    (state) => state.bulkUpdateIssueDueDate,
  );

  useEffect(() => {
    if (currentProject && !currentProjectIssues) {
      fetchIssuesForProject(supabaseClient, currentProject.id, false);
    }
  }, [currentProject]);

  // Initialize the global status icon store ONCE at startup
  useEffect(() => {
    useStatusIconStore.getState().initializeIcons();
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get("project_id");
  const { registerShortcut, unregisterShortcut } = useShortcuts();

  const prevProjectId = useRef(selectedProjectId);

  useEffect(() => {
    if (
      selectedProjectId &&
      supabaseClient &&
      prevProjectId.current !== selectedProjectId
    ) {
      prevProjectId.current = selectedProjectId;
      fetchIssuesForProject(supabaseClient, selectedProjectId, true);
    }
  }, [selectedProjectId, supabaseClient, fetchIssuesForProject]);

  // Refetch issues when PM Agent issue tools complete
  const refetchIssuesTrigger = useDatabaseSyncWrapperStore(
    (state) => state.refetchIssuesTrigger,
  );
  useEffect(() => {
    if (refetchIssuesTrigger > 0 && selectedProjectId && supabaseClient) {
      fetchIssuesForProject(supabaseClient, selectedProjectId, true);
    }
  }, [
    refetchIssuesTrigger,
    selectedProjectId,
    supabaseClient,
    fetchIssuesForProject,
  ]);

  const selectedIssueIds = useIssueSelectionStore(
    (state) => state.selectedIssueIds,
  );
  const clearSelection = useIssueSelectionStore(
    (state) => state.clearSelection,
  );
  const clearSelectionOnly = useIssueSelectionStore(
    (state) => state.clearSelectionOnly,
  );
  const toggleIssueSelection = useIssueSelectionStore(
    (state) => state.toggleIssueSelection,
  );
  const selectAllVisible = useIssueSelectionStore(
    (state) => state.selectAllVisible,
  );
  const selectAllInColumn = useIssueSelectionStore(
    (state) => state.selectAllInColumn,
  );
  const lastSelectedIssueId = useIssueSelectionStore(
    (state) => state.lastSelectedIssueId,
  );

  const setFocusedIssue = useFocusStore((state) => state.setFocusedIssue);
  const setLastFocusedIssue = useFocusStore(
    (state) => state.setLastFocusedIssue,
  );
  const clearFocus = useFocusStore((state) => state.clearFocus);
  const focusedIssueId = useFocusStore((state) => state.focusedIssueId);
  const lastFocusedIssueId = useFocusStore((state) => state.lastFocusedIssueId);
  const resetFocus = useFocusStore((state) => state.resetFocus);

  // Clear focus states when project changes - moved this earlier
  useEffect(() => {
    resetFocus();
  }, [selectedProjectId]);

  const [showNoRepoModal, setShowNoRepoModal] = React.useState(false);
  const [showCreateRepoModal, setShowCreateRepoModal] = React.useState(false);
  const [originatingGitProvider, setOriginatingGitProvider] =
    React.useState<GitProvider>(GitProvider.GITHUB);
  const [loadingSettled, setLoadingSettled] = React.useState(false);
  const { isOpen, setIsOpen } = useCreateCanvasModal();

  // Get custom project statuses from the project prop (authoritative for this view)
  const customProjectStatuses = useMemo(() => {
    return project?.statuses ?? [];
  }, [project?.statuses]);

  // Create comprehensive status list for StatusBadge component
  const { comprehensiveStatusList } = useCanvasStatusManagement();

  const { getStatusIcon, updateCustomStatusIcons } = useStatusIconStore();

  // Update custom status icons in global store when they change
  useEffect(() => {
    if (customProjectStatuses.length > 0) {
      updateCustomStatusIcons(customProjectStatuses);
    }
  }, [customProjectStatuses, updateCustomStatusIcons]);

  // Combine default and custom statuses for sorting using column config
  const allProjectStatuses = useMemo(() => {
    const columnConfig = getColumnConfig(customProjectStatuses);

    // Convert column config to the format expected by allProjectStatuses
    return columnConfig.map((config) => ({
      key: config.key,
      status: config.name,
      isDefault: config.isDefault,
      type: config.type,
    }));
  }, [customProjectStatuses]);

  // Memoized helper function to check if a status name is a default status (original or renamed)
  const isDefaultStatusName = useCallback(
    (statusName: string): boolean => {
      return isDefaultStatusNameUtil(statusName, allProjectStatuses);
    },
    [allProjectStatuses],
  );

  // Determine if project has a repo
  const hasRepo = React.useMemo(() => {
    if (project && typeof project.repoPath === "string") {
      const repoPath = project.repoPath.toLowerCase();
      // Check if project has a real repository (not a template)
      const isTemplateProject = TEMPLATE_PROJECT_PATHS.some(
        (templatePath) => templatePath === repoPath,
      );

      return !isTemplateProject;
    }
    return false;
  }, [project]);

  const isLoading = React.useMemo(() => {
    if (!selectedProjectId) return false;
    if (!project) return true;
    if (!currentProjectIssues) return true;

    const statusProcessingComplete =
      isProjectStatusProcessingComplete(selectedProjectId);

    if (!statusProcessingComplete) {
      return true;
    }

    return false;
  }, [
    selectedProjectId,
    project,
    currentProjectIssues,
    isProjectStatusProcessingComplete,
  ]);

  // Reset loading state only when project actually changes
  useEffect(() => {
    if (prevProjectId.current !== selectedProjectId) {
      setLoadingSettled(false);

      if (selectedProjectId) {
        useIssuesStore.setState((state) => {
          const { [selectedProjectId]: _, ...remainingProjects } =
            state.issuesByProject;

          const projectIssues = state.issuesByProject[selectedProjectId];
          const remainingCanvasIssues = Object.fromEntries(
            Object.entries(state.canvasIdToIssue).filter(([canvasId]) => {
              return !projectIssues || !projectIssues[canvasId];
            }),
          );

          return {
            projectStatusProcessingComplete: {
              ...state.projectStatusProcessingComplete,
              [selectedProjectId]: false,
            },
            issuesByProject: remainingProjects,
            canvasIdToIssue: remainingCanvasIssues,
          };
        });
      }

      prevProjectId.current = selectedProjectId;
    }
  }, [selectedProjectId]);

  // Set loading settled when all data and processing is complete
  useEffect(() => {
    if (!isLoading && !loadingSettled) {
      setLoadingSettled(true);
    }
  }, [isLoading, loadingSettled]);

  const shouldShowLoading = isLoading || !loadingSettled;

  const checkStatusMatch = useCallback(
    (issueStatus: string | undefined) => {
      if (!filters?.statuses?.length) return true;

      if (!issueStatus)
        return filters.statuses.includes(STATE_KEY_TO_STATUS_ID.BACKLOG);

      const availableStatusNames = allProjectStatuses.map(
        (status) => status.status,
      );
      const resolvedStatus = resolveIssueStatus(
        issueStatus,
        availableStatusNames,
      );

      const defaultStatusKey = Object.keys(KanbanDefaultStates).find(
        (key) => KanbanDefaultStates[key] === resolvedStatus,
      );

      let currentStatus: string;
      if (defaultStatusKey) {
        currentStatus =
          STATE_KEY_TO_STATUS_ID[defaultStatusKey] ||
          STATE_KEY_TO_STATUS_ID.BACKLOG;
      } else {
        currentStatus = resolvedStatus;
      }

      const baseMatch = filters.statuses.includes(currentStatus);
      const condition = filters?.conditions?.statuses;
      const isConsolidated = filters.statuses.length > 1;
      return applyFilterCondition(baseMatch, condition, isConsolidated);
    },
    [filters?.statuses, filters?.conditions?.statuses, allProjectStatuses],
  );

  const applyFilterCondition = (
    matches: boolean,
    condition: string | undefined,
    isConsolidated: boolean = false,
  ) => {
    if (!condition) return matches;

    if (isConsolidated) {
      switch (condition) {
        case FilterConditionConsolidated.IS_ANY_OF:
          return matches;
        case FilterConditionConsolidated.IS_NOT:
          return !matches;
        default:
          return matches;
      }
    } else {
      switch (condition) {
        case FilterCondition.IS:
          return matches;
        case FilterCondition.IS_NOT:
          return !matches;
        default:
          return matches;
      }
    }
  };

  // Filter for the canvases
  const issuesToShow = React.useMemo(() => {
    if (!selectedProjectId) return [];
    if (!project) return [];
    if (!currentProjectIssues) return [];

    return Object.values(currentProjectIssues).filter((issue) => {
      const projectIdMatch = issue.canvas.project_id === selectedProjectId;
      const prd = issue.featurePrd;
      const searchMatch = filters?.search
        ? (prd?.title || issue.canvas.current_branch || project?.name)
            ?.toLowerCase()
            ?.includes(filters?.search.toLowerCase())
        : true;

      const userIdMatch = (() => {
        if (!filters?.userIds.length) return true;

        const baseMatch = filters.userIds.some((userId) => {
          if (userId === "none") {
            return !issue.canvas.assigned_user_id;
          }
          return issue.canvas.assigned_user_id === userId;
        });

        const condition = filters?.conditions?.userIds;
        const isConsolidated = filters.userIds.length > 1;
        return applyFilterCondition(baseMatch, condition, isConsolidated);
      })();
      const labelsMatch = (() => {
        if (!filters?.labels.length) return true;

        const baseMatch = filters.labels.some((filterLabel) => {
          if (filterLabel === "none") {
            return !issue.canvas.labels?.length;
          }
          return issue.canvas.labels?.includes(filterLabel);
        });

        const condition = filters?.conditions?.labels;
        const isConsolidated = filters.labels.length > 1;
        return applyFilterCondition(baseMatch, condition, isConsolidated);
      })();

      const statusMatch = checkStatusMatch(prd?.status || undefined);
      const estimateMatch = (() => {
        if (!filters?.estimates?.length) return true;
        const prdEstimate = prd?.estimate;
        const baseMatch =
          (filters.estimates.includes(0) && !prdEstimate) ||
          filters.estimates.includes(prdEstimate || 0);

        const condition = filters?.conditions?.estimates;
        const isConsolidated = filters.estimates.length > 1;
        return applyFilterCondition(baseMatch, condition, isConsolidated);
      })();
      const priorityMatch = (() => {
        if (!filters?.priorities?.length) return true;
        const baseMatch = filters.priorities.includes(prd?.priority || 0);

        const condition = filters?.conditions?.priorities;
        const isConsolidated = filters.priorities.length > 1;
        return applyFilterCondition(baseMatch, condition, isConsolidated);
      })();
      const dueDateMatch = (() => {
        if (!filters?.dueDates) return true;
        const dueDate = prd?.due_date ? new Date(prd.due_date) : null;

        // If no filters are selected, show all items
        if (
          !filters.dueDates.none &&
          !filters.dueDates.overdue &&
          !filters.dueDates.today &&
          !filters.dueDates.thisWeek &&
          !filters.dueDates.nextWeek &&
          !filters.dueDates.later
        ) {
          return true;
        }

        // If the item has no due date, only show it if "none" filter is selected
        if (!dueDate) {
          const baseMatch = filters.dueDates.none || false;
          const condition = filters?.conditions?.dueDates;
          const isConsolidated =
            Object.values(filters.dueDates).filter(Boolean).length > 1;
          return applyFilterCondition(baseMatch, condition, isConsolidated);
        }

        const today = startOfDay(new Date());

        // Check each selected filter with OR logic
        const baseMatch =
          // Overdue
          ((filters.dueDates.overdue || false) &&
            isPast(dueDate) &&
            !isSameDay(dueDate, today)) ||
          // Today
          ((filters.dueDates.today || false) && isSameDay(dueDate, today)) ||
          // This week
          ((filters.dueDates.thisWeek || false) &&
            isWithinInterval(dueDate, {
              start: today,
              end: addDays(today, 7),
            })) ||
          // Next week
          ((filters.dueDates.nextWeek || false) &&
            isWithinInterval(dueDate, {
              start: addDays(today, 7),
              end: addDays(today, 14),
            })) ||
          // Later
          ((filters.dueDates.later || false) && dueDate > addDays(today, 14));

        const condition = filters?.conditions?.dueDates;
        const isConsolidated =
          Object.values(filters.dueDates).filter(Boolean).length > 1;
        return applyFilterCondition(baseMatch, condition, isConsolidated);
      })();
      const runningBaseAndNotMain =
        userId &&
        ADMIN_USER_IDS.includes(userId) &&
        project?.name?.toLowerCase().includes("running base") &&
        issue.canvas.current_branch !== "MAIN" &&
        !prd?.title?.startsWith("MAIN");

      const result =
        projectIdMatch &&
        searchMatch &&
        userIdMatch &&
        labelsMatch &&
        statusMatch &&
        estimateMatch &&
        priorityMatch &&
        dueDateMatch &&
        !runningBaseAndNotMain;

      return result;
    });
  }, [
    selectedProjectId,
    filters,
    project,
    project?.users,
    userId,
    currentProjectIssues,
  ]);

  // Extract sorting function for reuse
  const sortColumnIssues = useCallback((issues: Issue[]) => {
    return [...issues].sort((a, b) => {
      const prdA = a.featurePrd;
      const prdB = b.featurePrd;

      // If both have orders, use them
      if (prdA?.order !== undefined && prdB?.order !== undefined) {
        return prdA.order - prdB.order;
      }

      // If only one has order, prioritize the ordered one
      if (prdA?.order !== undefined) return -1;
      if (prdB?.order !== undefined) return 1;

      // Neither has order, sort by last_updated (most recent first)
      const timeA = a.canvas?.last_updated || "-1";
      const timeB = b.canvas?.last_updated || "-1";
      return timeB.localeCompare(timeA);
    });
  }, []);

  const stateToIssues = React.useMemo(() => {
    const columns: Record<string, StatusColumn> = allProjectStatuses.reduce(
      (acc, statusItem) => {
        acc[statusItem.status] = {
          stateKey: statusItem.key,
          title: statusItem.status,
          issues: [],
        };
        return acc;
      },
      {} as Record<string, StatusColumn>,
    );

    const defaultStatusNameMap = new Map<string, string>();
    allProjectStatuses.forEach((statusItem) => {
      if (statusItem.isDefault) {
        const defaultStatus = customProjectStatuses.find(
          (s) =>
            s.isDefault &&
            (s.id === statusItem.key || s.defaultStatusKey === statusItem.key),
        );

        let originalDefaultName: string | undefined;
        if (defaultStatus?.defaultStatusKey) {
          originalDefaultName =
            KanbanDefaultStates[
              defaultStatus.defaultStatusKey as keyof typeof KanbanDefaultStates
            ];
        } else {
          originalDefaultName =
            KanbanDefaultStates[
              statusItem.key as keyof typeof KanbanDefaultStates
            ];
        }

        if (originalDefaultName && originalDefaultName !== statusItem.status) {
          defaultStatusNameMap.set(originalDefaultName, statusItem.status);
        }
        defaultStatusNameMap.set(statusItem.status, statusItem.status);
      }
    });

    const availableStatusNames = allProjectStatuses.map(
      (statusItem) => statusItem.status,
    );

    issuesToShow.forEach((issue) => {
      let resolvedStatus = resolveIssueStatus(
        issue.featurePrd?.status,
        availableStatusNames,
      );

      if (defaultStatusNameMap.has(resolvedStatus)) {
        resolvedStatus = defaultStatusNameMap.get(resolvedStatus)!;
      }

      const targetColumn =
        columns[resolvedStatus] || columns[KanbanDefaultStates.BACKLOG];

      if (targetColumn) {
        targetColumn.issues.push(issue);
      }
    });

    // Sort all columns
    Object.values(columns).forEach((column: StatusColumn) => {
      column.issues = sortColumnIssues(column.issues);
    });

    return columns;
  }, [issuesToShow, sortColumnIssues, allProjectStatuses]);

  const statusIconMap = useMemo(() => {
    const iconMap = new Map<string, (() => JSX.Element) | undefined>();

    const resolveCustomStatusIcon = (
      customType: string,
    ): (() => JSX.Element) | undefined => {
      let parentStatus = STATUS_LIST.find((s) => s.id === customType);

      if (!parentStatus) {
        const mappedType =
          STATUS_TYPE_MAPPING[customType as keyof typeof STATUS_TYPE_MAPPING];
        if (mappedType) {
          parentStatus = STATUS_LIST.find((s) => s.id === mappedType);
        }
      }

      return parentStatus?.icon || getStatusIcon(KanbanDefaultStates.BACKLOG);
    };

    allProjectStatuses.forEach((statusItem) => {
      let icon: (() => JSX.Element) | undefined;

      if (statusItem.isDefault) {
        const statusFromList = STATUS_LIST.find(
          (s) => s.id === statusItem.type,
        );
        if (statusFromList) {
          icon = statusFromList.icon;
        } else {
          const defaultStatus = customProjectStatuses.find(
            (s) =>
              s.isDefault &&
              (s.id === statusItem.key ||
                s.defaultStatusKey === statusItem.key),
          );

          let originalDefaultName: string | undefined;
          if (defaultStatus?.defaultStatusKey) {
            originalDefaultName =
              KanbanDefaultStates[
                defaultStatus.defaultStatusKey as keyof typeof KanbanDefaultStates
              ];
          } else {
            const enumKey = Object.keys(KanbanDefaultStates).find(
              (key) =>
                KanbanDefaultStates[key as keyof typeof KanbanDefaultStates] ===
                statusItem.status,
            );
            if (enumKey) {
              originalDefaultName =
                KanbanDefaultStates[
                  enumKey as keyof typeof KanbanDefaultStates
                ];
            }
          }

          if (originalDefaultName) {
            icon = getStatusIcon(originalDefaultName);
          } else {
            icon = getStatusIcon(statusItem.status);
          }
        }
      } else {
        const customStatus = customProjectStatuses.find(
          (s) => s?.id === statusItem.key,
        );
        if (customStatus) {
          icon = resolveCustomStatusIcon(customStatus.type);
        }
      }

      if (icon) {
        iconMap.set(statusItem.status, icon);
        iconMap.set(statusItem.key, icon);
        if (statusItem.isDefault) {
          const defaultStatus = customProjectStatuses.find(
            (s) =>
              s.isDefault &&
              (s.id === statusItem.key ||
                s.defaultStatusKey === statusItem.key),
          );
          if (defaultStatus?.defaultStatusKey) {
            iconMap.set(defaultStatus.defaultStatusKey, icon);
            const originalName =
              KanbanDefaultStates[
                defaultStatus.defaultStatusKey as keyof typeof KanbanDefaultStates
              ];
            if (originalName) {
              iconMap.set(originalName, icon);
            }
          }
          const enumKey = Object.keys(KanbanDefaultStates).find(
            (key) =>
              KanbanDefaultStates[key as keyof typeof KanbanDefaultStates] ===
              statusItem.status,
          );
          if (enumKey) {
            iconMap.set(enumKey, icon);
          }
        }
      }
    });

    return iconMap;
  }, [allProjectStatuses, customProjectStatuses, getStatusIcon]);

  const issueStatusIconMap = useMemo(() => {
    const iconMap = new Map<string, (() => JSX.Element) | undefined>();
    const availableStatusNames = allProjectStatuses.map(
      (statusItem) => statusItem.status,
    );

    issuesToShow.forEach((issue) => {
      const resolvedStatus = resolveIssueStatus(
        issue.featurePrd?.status,
        availableStatusNames,
      );

      const icon = statusIconMap.get(resolvedStatus);
      if (icon) {
        iconMap.set(issue.canvas.id, icon);
      }
    });

    return iconMap;
  }, [issuesToShow, statusIconMap, allProjectStatuses]);

  const [isDragging, setIsDragging] = React.useState(false);

  const scrollToStatus = useLayoutStore((state) => state.scrollToStatus);
  const setScrollToStatus = useLayoutStore((state) => state.setScrollToStatus);

  const onDragEnd = React.useCallback(
    async (result) => {
      setIsDragging(false);
      const { destination, source, draggableId } = result;

      if (!destination || !supabaseClient) return;

      const stateKey = destination.droppableId;
      const issueDropping = issuesToShow.find(
        (issue) => issue.canvas.id === draggableId,
      );

      // Return early if we can't find the issue
      if (!issueDropping) {
        console.error("Could not find issue with id:", draggableId);
        return;
      }

      const issueDroppingStatus =
        issueDropping?.featurePrd?.status || KanbanDefaultStates.BACKLOG;

      const targetStatusName = getStatusNameFromStateKeyUtil(
        stateKey,
        customProjectStatuses,
        allProjectStatuses,
        stateToIssues,
      );

      // getStatusNameFromStateKey always returns a string (fallback to BACKLOG),
      // but validate it's not empty for safety
      if (!targetStatusName || targetStatusName.trim() === "") {
        toast.error("Failed to update status. Invalid status column.");
        return;
      }

      const originalStatus =
        issueDropping?.featurePrd?.status || KanbanDefaultStates.BACKLOG;

      // Check if this is a default status (either original name or renamed)
      const isDefaultStatus = isDefaultStatusName(targetStatusName);

      // if there is a selection, grab the selected issues
      // else select the issue dropping
      const dragIssues =
        selectedIssueIds.size > 0
          ? [...selectedIssueIds].reduce((acc: Issue[], id) => {
              const issue = currentProjectIssues[id];
              if (issue) acc.push(issue);
              return acc;
            }, [])
          : [issueDropping];

      // split selected issues by status
      const dragIssuesByStatus = dragIssues.reduce((acc, issue) => {
        // Ensure we have a valid status - default to Backlog if no status exists
        const status = issue.featurePrd?.status || KanbanDefaultStates.BACKLOG;
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push(issue);
        return acc;
      }, {});

      // get the status columns with drag issues removed
      const statusColumnsWithoutDragIssues: Map<string, Issue[]> = new Map();
      Object.keys(dragIssuesByStatus).forEach((status) => {
        const dragStatusIssues = dragIssuesByStatus[status];
        // Convert drag issues to a set of IDs for faster lookup
        const dragStatusIssueIds = new Set(
          dragStatusIssues.map((issue) => issue.canvas.id),
        );

        const statusIssues = (stateToIssues[status]?.issues || []).filter(
          (issue) => !dragStatusIssueIds.has(issue.canvas.id),
        );

        statusColumnsWithoutDragIssues.set(status, statusIssues);
      });

      // Now we have a list of drag issues, and a list of status columns without the drag issues
      // Now we merge our drag issues into the destination column in the correct index
      const mergedDragIssues: Issue[] = [];
      // the head of the mergedDragIssues is the draggable's column
      mergedDragIssues.push(...dragIssuesByStatus[issueDroppingStatus]);
      // find the original drag issue in the mergedDragIssues and move it to the front
      const originalDragIssueIndex = mergedDragIssues.findIndex(
        (issue) => issue.canvas.id === issueDropping.canvas.id,
      );
      if (originalDragIssueIndex !== -1) {
        mergedDragIssues.unshift(
          mergedDragIssues.splice(originalDragIssueIndex, 1)[0],
        );
      }
      // Now go through the other status columns and add the issues to the mergedDragIssues
      Object.keys(dragIssuesByStatus).forEach((status) => {
        // skip the status that we already did
        if (status === issueDroppingStatus) {
          return;
        }
        mergedDragIssues.push(...dragIssuesByStatus[status]);
      });

      // insert our issues into the destination column at destination index
      const statusColumnsFinalState: Map<string, Issue[]> = new Map();
      // add the without drag issues to the map
      statusColumnsWithoutDragIssues.forEach((issues, status) => {
        statusColumnsFinalState.set(status, issues);
      });
      // add the destination column to the map
      if (!statusColumnsFinalState.has(targetStatusName)) {
        statusColumnsFinalState.set(targetStatusName, [
          ...(stateToIssues[targetStatusName]?.issues || []),
        ]);
      }
      const destinationColumn = statusColumnsFinalState.get(targetStatusName);
      if (!destinationColumn) {
        console.error("Could not find destination column:", targetStatusName);
        return;
      }
      destinationColumn.splice(destination.index, 0, ...mergedDragIssues);

      // now that we have the full state of all changed columns, we must go through each column and update the order of the issues
      statusColumnsFinalState.forEach((issues, status) => {
        issues.forEach((issue, index) => {
          // Ensure we're using the correct status name for the destination column
          const finalStatus =
            status === targetStatusName ? targetStatusName : status;

          if (issue.featurePrd) {
            // Update existing PRD
            useIssuesStore.getState().updateFeaturePRD(supabaseClient, {
              id: issue.featurePrd?.id,
              canvas_id: issue.canvas.id,
              order: index,
              status: finalStatus,
            });
          } else {
            // Create new PRD
            useIssuesStore.getState().createFeaturePRD(supabaseClient, {
              id: crypto.randomUUID(),
              canvas_id: issue.canvas.id,
              order: index,
              status: finalStatus,
            });
          }
        });
      });

      // For custom statuses, validate after the update and rollback if needed
      // Default statuses (including renamed ones) can't be deleted, so no validation needed
      if (!isDefaultStatus) {
        // Ensure we have a valid project ID before validation
        if (!selectedProjectId) {
          toast.error("Cannot validate status - no project selected");
          return;
        }

        const validation = await validateCustomStatus(
          selectedProjectId,
          targetStatusName,
          isDefaultStatus,
        );

        if (!validation.isValid) {
          const rollbackPrd = {
            id: issueDropping?.featurePrd?.id,
            canvas_id: issueDropping?.canvas.id,
            status: originalStatus,
          };

          try {
            // Use individual rollback call
            if (rollbackPrd.id) {
              useIssuesStore
                .getState()
                .updateFeaturePRD(supabaseClient, rollbackPrd);
            } else {
              useIssuesStore.getState().createFeaturePRD(supabaseClient, {
                ...rollbackPrd,
                id: crypto.randomUUID(),
              });
            }

            toast.error(
              validation.error ||
                "Cannot update to this status - it has been deleted or updated. Canvas moved back to previous column.",
            );

            if (currentProject) {
              fetchIssuesForProject(supabaseClient, currentProject.id, false);
            }
          } catch (rollbackError) {
            console.error("Error rolling back status:", rollbackError);
            toast.error(
              "Status validation failed and rollback failed. Please refresh the page.",
            );
          }
        }
      }
    },
    [
      stateToIssues,
      issuesToShow,
      supabaseClient,
      selectedIssueIds,
      currentProjectIssues,
      customProjectStatuses,
      allProjectStatuses,
      isDefaultStatusName,
      selectedProjectId,
      currentProject,
      fetchIssuesForProject,
    ],
  );

  const onDragStart = (result: any) => {
    if (result.draggableId) {
      // if draggableId is not one of the selected issue ids, clear the issue selection
      if (!selectedIssueIds.has(result.draggableId)) {
        clearSelection();
      }
    }
    setIsDragging(true);
  };

  const handleCreateRepository = (provider: "github" | "gitlab") => {
    const gitProvider =
      provider === "github" ? GitProvider.GITHUB : GitProvider.GITLAB;
    setOriginatingGitProvider(gitProvider);
    setShowNoRepoModal(false);
    setShowCreateRepoModal(true);
  };

  const handleButtonClick = useCallback(
    (key: string) => {
      if (hasRepo || issuesToShow.length !== 1) {
        // If repo exists, trigger create canvas modal
        setIsOpen(key);
        useProjectModalStore.getState().addProjectModal("create-canvas-modal");
      } else {
        // If no repo and canvases.length is 1, show repository connection modal
        setShowNoRepoModal(true);
      }
    },
    [hasRepo, issuesToShow, setIsOpen],
  );

  function assignUserToIssues(userId: string | null) {
    if (selectedIssueIds.size === 0) return;

    const user = userId
      ? (project?.users as any[])?.find((u) => u.id === userId) || null
      : null;
    bulkUpdateIssueAssignee(supabaseClient, Array.from(selectedIssueIds), user);
  }

  function changePriorityOfIssues(priority: string | null) {
    if (selectedIssueIds.size === 0) return;

    const priorityId = priority ? parseInt(priority, 10) : null;
    bulkUpdateIssuePriority(
      supabaseClient,
      Array.from(selectedIssueIds),
      priorityId,
    );
  }

  const changeStatusOfIssues = async (statusId: string | null) => {
    if (!statusId || selectedIssueIds.size === 0) return;

    const originalStatuses = Array.from(selectedIssueIds).map((id) => {
      const issue = currentProjectIssues[id];
      return {
        issueId: id,
        originalStatus:
          issue?.featurePrd?.status || KanbanDefaultStates.BACKLOG,
      };
    });

    // For custom statuses, we'll validate after the update and rollback if needed
    const isDefaultStatus = isDefaultStatusName(statusId);

    bulkUpdateIssueStatus(
      supabaseClient,
      Array.from(selectedIssueIds),
      statusId,
    );

    // For custom statuses, validate after the update and rollback if needed
    if (!isDefaultStatus) {
      // Ensure we have a valid project ID before validation
      if (!selectedProjectId) {
        toast.error("Cannot validate status - no project selected");
        return;
      }

      const validation = await validateCustomStatus(
        selectedProjectId,
        statusId,
        isDefaultStatus,
      );

      if (!validation.isValid) {
        const rollbackPrds = originalStatuses.map(
          ({ issueId, originalStatus }) => ({
            canvas_id: issueId,
            status: originalStatus,
          }),
        );

        try {
          // Use individual rollback calls for each PRD
          rollbackPrds.forEach((rollbackPrd) => {
            if (rollbackPrd.canvas_id) {
              useIssuesStore.getState().updateFeaturePRD(supabaseClient, {
                canvas_id: rollbackPrd.canvas_id,
                status: rollbackPrd.status,
              });
            }
          });

          toast.error(
            validation.error ||
              "Cannot update to this status - it has been deleted or updated. Issues moved back to previous statuses.",
          );

          if (currentProject) {
            fetchIssuesForProject(supabaseClient, currentProject.id, false);
          }
        } catch (rollbackError) {
          console.error("Error rolling back statuses:", rollbackError);
          toast.error(
            "Status validation failed and rollback failed. Please refresh the page.",
          );
        }
      }
    }
  };

  const setDueDateOfIssues = (date: string | null) => {
    if (selectedIssueIds.size === 0) return;
    bulkUpdateIssueDueDate(supabaseClient, Array.from(selectedIssueIds), date);
  };

  const setLabelOfIssues = (labelId: string | null) => {
    if (selectedIssueIds.size === 0) {
      return;
    }

    let labelInterface: LabelInterface | null = null;
    if (labelId === null) {
      labelInterface = null;
    } else {
      const label = (project?.labels as any[])?.find((l) => l.id === labelId);

      if (label) {
        labelInterface = {
          id: label.id,
          label: label.name,
          color: label.color,
          name: label.name,
        };
      }
    }

    // now decide if we need to add or remove the label
    const selectedIssues = Array.from(selectedIssueIds).map(
      (id) => currentProjectIssues[id],
    );
    const issueLabels = selectedIssues.map((issue) => {
      const labels = issue.canvas.labels;

      if (Array.isArray(labels)) {
        return {
          issueId: issue.canvas.id,
          labels: labels,
        };
      }

      return {
        issueId: issue.canvas.id,
        labels: [],
      };
    });

    if (labelInterface === null) {
      // set all issues to have no label
      issueLabels.forEach((entry) => {
        entry.labels = [];
      });

      const issueIdToLabelInterfaces: {
        issueId: string;
        labels: LabelInterface[];
      }[] = [];
      issueLabels.forEach((entry) => {
        issueIdToLabelInterfaces.push({ issueId: entry.issueId, labels: [] });
      });

      bulkUpdateIssueLabel(supabaseClient, issueIdToLabelInterfaces);
      return;
    }

    // go through each entry in the issueLabels array
    let allIssuesHaveLabel = true;
    issueLabels.forEach((entry) => {
      const labels = entry.labels;
      let hasLabel = false;

      if (labelId === null && labels.length === 0) {
        hasLabel = true;
      } else {
        labels.forEach((label) => {
          if (label === labelId) {
            hasLabel = true;
          }
        });
      }

      if (!hasLabel) {
        allIssuesHaveLabel = false;
      }
    });

    const modifiedIssueLabels: { issueId: string; labels: string[] }[] = [];

    if (allIssuesHaveLabel) {
      // remove the label from the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        newLabels.splice(newLabels.indexOf(labelInterface.id), 1);
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    } else {
      // add the label to the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        // only add the label if it's not already in the list
        if (newLabels.indexOf(labelInterface.id) === -1) {
          newLabels.push(labelInterface.id);
        }
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    }

    // convert modifiedIssueLabels to issueIdToLabelInterfaces using the project labels
    const issueIdToLabelInterfaces: {
      issueId: string;
      labels: LabelInterface[];
    }[] = [];
    modifiedIssueLabels.forEach((entry) => {
      const labels = entry.labels;
      const labelInterfaces: LabelInterface[] = labels
        .map((label) => {
          const labelInterface = (project?.labels as any[])?.find(
            (l) => l.id === label,
          );
          if (labelInterface) {
            return labelInterface;
          }
          return null;
        })
        .filter((label) => label !== null);
      issueIdToLabelInterfaces.push({
        issueId: entry.issueId,
        labels: labelInterfaces,
      });
    });

    bulkUpdateIssueLabel(supabaseClient, issueIdToLabelInterfaces);
  };

  function getOpenCanvasState(): string | null {
    if (!isOpen) return null;

    // Check if it's a default status key (like "IN_PROGRESS")
    if (KanbanDefaultStates[isOpen as keyof typeof KanbanDefaultStates]) {
      return KanbanDefaultStates[isOpen as keyof typeof KanbanDefaultStates];
    }

    // Check if it's a custom status ID
    if (Array.isArray(project?.statuses)) {
      const customStatus = project.statuses?.find(
        (status) => status?.id === isOpen,
      );
      if (customStatus?.name) {
        return customStatus.name;
      }
    }

    // Check if it's a status ID from STATUS_LIST
    const statusObj = STATUS_LIST.find((status) => status.id === isOpen);
    if (statusObj) {
      return statusObj.name;
    }

    return isOpen;
  }

  const changeEstimateOfIssues = (estimate: number | null) => {
    if (selectedIssueIds.size === 0) {
      return;
    }

    bulkUpdateIssueEstimate(
      supabaseClient,
      Array.from(selectedIssueIds),
      estimate,
    );
  };

  const {
    registerCategory,
    showCategory,
    hideCategory,
    open: openCommandPalette,
    setMode,
    setSubMenuConfig,
  } = useCommandPalette();

  const issueSelectionOptions = useMemo<CommandPaletteOption[]>(
    () =>
      [
        {
          icon: <User className="size-4" />,
          name: "Assign to me",
          shortcut: ["I"],
          registerKeys: "i",
          hasFollowUp: false,
          preventDefault: true,
          action: () => {
            assignUserToIssues(userId ?? null);
          },
        },
        {
          icon: <UserPlus className="size-4" />,
          name: "Assign to",
          shortcut: ["A"],
          registerKeys: "a",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Assign to...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const assigneeId = issue?.canvas?.assigned_user_id ?? null;
              return assigneeId === value;
            },
            items: (() => {
              const assignOptions: CommandPaletteSubMenuItem[] = (
                project?.users as any[]
              )?.map((user: any) => ({
                name: user.name,
                value: user.id,
                icon: (
                  <UserAvatar
                    userName={user.name || user.email}
                    userId={user.id}
                  />
                ),
              }));

              if (!assignOptions) {
                // users is reliant on API, so we don't want to show anything if it's not loaded yet
                return [];
              }

              assignOptions.sort((a, b) => a.name?.localeCompare(b.name ?? ""));

              assignOptions.unshift({
                name: "Unassigned",
                value: null,
                icon: <UserCircle className="size-4" />,
              });

              return assignOptions;
            })(),
            action: (value) => {
              assignUserToIssues(value);
            },
          },
        },
        {
          icon: <BarChart className="size-4" />,
          name: "Change Priority",
          shortcut: ["P"],
          registerKeys: "p",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Priority...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const priorityId = issue?.featurePrd?.priority || 0;
              return priorityId === parseInt(value ?? "0");
            },
            items: (() => {
              const priorityOptions: CommandPaletteSubMenuItem[] =
                defaultPriorities.map((priority) => ({
                  name: priority.name,
                  value: priority.id.toString(),
                  icon: <priority.icon className="size-4" />,
                }));

              priorityOptions.sort(
                (a, b) => parseInt(b.value ?? "0") - parseInt(a.value ?? "0"),
              );

              priorityOptions.unshift({
                name: "No Priority",
                value: null,
                icon: <Minus className="size-4" />,
              });

              return priorityOptions;
            })(),
            action: (value) => {
              changePriorityOfIssues(value);
            },
          },
        },
        {
          icon: <CircleDashed className="size-4" />,
          name: "Change Status",
          shortcut: ["S"],
          registerKeys: "s",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Status...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              return issue?.featurePrd?.status === value;
            },
            items: (() => {
              const statusOptions = comprehensiveStatusList.map((status) => {
                const Icon = status.icon;
                return {
                  name:
                    status.name.length > 20
                      ? `${status.name.substring(0, 17)}...`
                      : status.name,
                  value: status.name,
                  icon: <Icon />,
                };
              });

              return statusOptions;
            })(),
            action: (value) => {
              changeStatusOfIssues(value);
            },
          },
        },
        {
          icon: <Calendar className="size-4" />,
          name: "Remove Due Date",
          shortcut: [getModifierKey(), "SHIFT", "D"],
          registerKeys: "mod+shift+d",
          hasFollowUp: false,
          preventDefault: true,
          action: () => {
            setDueDateOfIssues(null);
          },
        },
        {
          icon: <Calendar className="size-4" />,
          name: "Set Due Date",
          shortcut: ["SHIFT", "D"],
          registerKeys: "shift+d",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.DATE_SELECTION,
            searchText: "Set Due Date...",
            items: (() => {
              const today = new Date();
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              const inAWeek = new Date(today);
              inAWeek.setDate(inAWeek.getDate() + 7);
              const dateOptions: CommandPaletteSubMenuItem[] = [
                {
                  name: "No Due Date",
                  value: null,
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "Today",
                  value: today.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "Tomorrow",
                  value: tomorrow.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
                {
                  name: "In a Week",
                  value: inAWeek.toISOString(),
                  icon: <Calendar className="size-4" />,
                },
              ];

              return dateOptions;
            })(),
            action: (value) => {
              setDueDateOfIssues(value);
            },
          },
        },
        {
          icon: <Tag className="size-4" />,
          name: "Set Labels",
          shortcut: ["L"],
          registerKeys: "l",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Set Labels...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const labels: string[] = Array.isArray(issue?.canvas?.labels)
                ? (issue.canvas.labels as string[])
                : [];
              if (value === null) {
                return labels.length === 0;
              }
              return labels.some((label) => label === value);
            },
            items: (() => {
              const labelsToDisplay = project?.labels as any[];
              if (!labelsToDisplay) {
                // labels is reliant on API, so we don't want to show anything if it's not loaded yet
                return [];
              }

              const labelOptions: CommandPaletteSubMenuItem[] =
                labelsToDisplay.map((label) => ({
                  name: label.name,
                  value: label.id,
                  icon: (
                    <div
                      className="size-2.5 rounded-[3px]"
                      style={{
                        backgroundColor: getColorValue(label.color),
                      }}
                    />
                  ),
                }));

              labelOptions.sort((a, b) => a.name?.localeCompare(b.name));

              labelOptions.unshift({
                name: "No Label",
                value: null,
                icon: <Tag className="size-4" />,
              });

              return labelOptions;
            })(),
            action: (value) => {
              setLabelOfIssues(value);
            },
          },
        },
        {
          icon: <HalfFilledTriangleIcon className="size-4" />,
          name: "Change Estimate",
          shortcut: ["SHIFT", "E"],
          registerKeys: "shift+e",
          hasFollowUp: true,
          preventDefault: true,
          subMenuConfig: {
            mode: CommandPaletteMode.ITEM_SELECTION,
            searchText: "Change Estimate...",
            selectionStatusFilter: (issue: Issue, value: string | null) => {
              const estimate = issue?.featurePrd?.estimate ?? null;
              const estimateValue = value ? parseInt(value) : null;
              return estimate === estimateValue;
            },
            items: (() => {
              const estimatesToDisplay: (number | null)[] = ESTIMATE_POINTS.map(
                (estimate) => {
                  const tShirtSize = getTShirtSize(estimate);
                  if (!tShirtSize) {
                    return null;
                  }
                  return estimate;
                },
              ).filter((estimate) => estimate !== null);

              const estimateOptions: CommandPaletteSubMenuItem[] =
                estimatesToDisplay.map((estimate) => ({
                  name: getTShirtSize(estimate) ?? "",
                  value: estimate?.toString() ?? "",
                  icon: <HalfFilledTriangleIcon className="size-4" />,
                }));

              estimateOptions.unshift({
                name: "No Estimate",
                value: null,
                icon: <TriangleDashed className="size-4" />,
              });

              return estimateOptions;
            })(),
            action: (value) => {
              changeEstimateOfIssues(value);
            },
          },
        },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [project?.users, selectedIssueIds],
  );

  useEffect(() => {
    registerCategory(OptionCategory.ISSUE_SELECTION, issueSelectionOptions);
  }, [issueSelectionOptions]);

  useEffect(() => {
    if (selectedIssueIds.size > 0) {
      showCategory(OptionCategory.ISSUE_SELECTION);
      issueSelectionOptions.forEach((option) => {
        if (option.subMenuConfig) {
          registerShortcut(
            option.registerKeys,
            (e) => {
              e.preventDefault();
              openCommandPalette();
              if (option.subMenuConfig) {
                setMode(option.subMenuConfig.mode);
                setSubMenuConfig(option.subMenuConfig);
              }
            },
            { preventDefault: option.preventDefault },
          );
        } else {
          registerShortcut(
            option.registerKeys,
            (e) => {
              e.preventDefault();
              option.action?.();
            },
            { preventDefault: option.preventDefault },
          );
        }
      });
    } else {
      hideCategory(OptionCategory.ISSUE_SELECTION);
      issueSelectionOptions.forEach((option) => {
        unregisterShortcut(option.registerKeys);
      });
    }
  }, [selectedIssueIds]);

  function fetchSelectableIssueIds() {
    const visibleIssueIds = issuesToShow.map((issue) => issue.canvas.id);

    let columnIssueIds: string[] = [];
    let targetIssueId: string | null = null;
    if (focusedIssueId) {
      targetIssueId = focusedIssueId;
    } else if (lastFocusedIssueId) {
      targetIssueId = lastFocusedIssueId;
    } else {
      targetIssueId = fetchTopLeftIssueId();
    }

    if (targetIssueId) {
      const focusedIssue = issuesToShow.find(
        (issue) => issue.canvas.id === targetIssueId,
      );
      if (focusedIssue) {
        const focusedPrd = focusedIssue.featurePrd;
        const focusedStatus = focusedPrd?.status || KanbanDefaultStates.BACKLOG;
        columnIssueIds = sortColumnIssues(
          issuesToShow.filter((issue) => {
            const prd = issue.featurePrd;
            return prd?.status === focusedStatus;
          }),
        ).map((issue) => issue.canvas.id);
      }
    }

    return {
      visibleIssueIds,
      columnIssueIds,
      focusedIssueId,
    };
  }

  function fetchTopLeftIssueId() {
    const orderedStatuses = allProjectStatuses.map(
      (statusItem) => statusItem.status,
    );

    // Find first column with issues
    for (const status of orderedStatuses) {
      const columnIssues = sortColumnIssues(
        issuesToShow.filter((issue) => {
          const prd = issue.featurePrd;
          return prd?.status === status;
        }),
      );

      if (columnIssues.length > 0) {
        return columnIssues[0].canvas.id;
      }
    }
    return null;
  }

  function handleSelectAllShortcut() {
    const { visibleIssueIds } = fetchSelectableIssueIds();
    selectAllVisible(visibleIssueIds);
  }

  function handleSelectAllInColumnShortcut() {
    const { columnIssueIds } = fetchSelectableIssueIds();
    selectAllInColumn(columnIssueIds);
  }

  function handleClearSelectionShortcut() {
    clearSelection();
  }

  function handleToggleSelectionShortcut(issueId: string | null = null) {
    if (issueId) {
      toggleIssueSelection(issueId);
      return;
    }

    const { focusedIssueId } = fetchSelectableIssueIds();

    if (focusedIssueId) {
      toggleIssueSelection(focusedIssueId);
    } else if (lastFocusedIssueId) {
      toggleIssueSelection(lastFocusedIssueId);
    } else {
      const topLeftIssueId = fetchTopLeftIssueId();
      if (topLeftIssueId) {
        toggleIssueSelection(topLeftIssueId);
      }
    }
  }

  // Issue keyboard shortcuts for reordering and status changes
  useIssueKeyboardShortcuts({
    stateToIssues,
    sortColumnIssues,
    selectedIssueIds: Array.from(selectedIssueIds),
    issuesToShow,
    focusedIssueId,
    viewType,
    isEnabled:
      !shouldShowLoading &&
      !useProjectModalStore.getState().hasActiveProjectModals(),
  });

  // Handle keyboard navigation and selection
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't handle keyboard navigation while loading or if any project modal is open
      if (
        shouldShowLoading ||
        useProjectModalStore.getState().hasActiveProjectModals()
      ) {
        return;
      }

      // Don't intercept shortcuts when user is focused on an input/textarea
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return;
      }

      const { visibleIssueIds, columnIssueIds, focusedIssueId } =
        fetchSelectableIssueIds();

      // Handle selection keyboard shortcuts first
      const isCmd = e.metaKey || e.ctrlKey;
      const isAlt = e.altKey;

      // TODO turn all of these into shortcuts, for now just wrap in a function
      if (isCmd && isAlt && e.code === "KeyA") {
        e.preventDefault();
        handleSelectAllInColumnShortcut();
        return;
      } else if (isCmd && e.code === "KeyA") {
        e.preventDefault();
        handleSelectAllShortcut();
        return;
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClearSelectionShortcut();
        clearFocus();
        return;
      } else if (e.code === "KeyX" && focusedIssueId) {
        e.preventDefault();
        handleToggleSelectionShortcut();
        return;
      }

      // Only handle arrow keys for navigation (non-Alt combinations)
      if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "j", "k"].includes(
          e.key,
        ) ||
        e.altKey // Skip Alt+Arrow combinations as they're handled by mousetrap
      ) {
        return;
      }

      e.preventDefault();

      let currentFocusedId = focusedIssueId;

      // If nothing is focused, check lastFocusedIssueId or find first issue in left-most column
      if (!currentFocusedId) {
        if (issuesToShow.length === 0) return;

        if (lastFocusedIssueId) {
          // Restore last focused issue if it still exists
          const issueExists = issuesToShow.some(
            (issue) => issue.canvas.id === lastFocusedIssueId,
          );
          if (issueExists) {
            currentFocusedId = lastFocusedIssueId;
          }
        }

        // If we couldn't restore last focused issue, find first issue in left-most column
        if (!currentFocusedId) {
          currentFocusedId = fetchTopLeftIssueId();
        }

        if (currentFocusedId) {
          setFocusedIssue(currentFocusedId);
        }
      }

      // Find the current issue's position and status
      const currentIssue = issuesToShow.find(
        (issue) => issue.canvas.id === currentFocusedId,
      );
      if (!currentIssue) return;

      const currentPrd = currentIssue.featurePrd;
      const currentStatus = currentPrd?.status || KanbanDefaultStates.BACKLOG;

      const orderedStatuses = allProjectStatuses.map(
        (statusItem) => statusItem.status,
      );
      const currentStatusIndex = orderedStatuses.indexOf(currentStatus);

      // TODO: turn these into shortcuts?
      let nextIndex;
      switch (e.key) {
        case "ArrowUp":
        case "ArrowDown":
        case "j":
        case "k": {
          // Find all issues in the current column
          const currentColumnIssues = sortColumnIssues(
            issuesToShow.filter((issue) => {
              const prd = issue.featurePrd;
              return prd?.status === currentStatus;
            }),
          );

          const currentPositionInColumn = currentColumnIssues.findIndex(
            (issue) => issue.canvas.id === currentFocusedId,
          );

          if (e.key === "ArrowUp" || e.key === "k") {
            // Try to move up in current column
            if (currentPositionInColumn > 0) {
              nextIndex =
                currentColumnIssues[currentPositionInColumn - 1].canvas.id;
            } else if (viewType === "list") {
              // Only in list view, look for previous column with issues
              let searchStatusIndex = currentStatusIndex - 1;
              while (searchStatusIndex >= 0) {
                const targetStatus = orderedStatuses[searchStatusIndex];
                const targetColumnIssues = sortColumnIssues(
                  issuesToShow.filter((issue) => {
                    const prd = issue.featurePrd;
                    return prd?.status === targetStatus;
                  }),
                );

                if (targetColumnIssues.length > 0) {
                  // Move to last issue in previous column with issues
                  nextIndex =
                    targetColumnIssues[targetColumnIssues.length - 1].canvas.id;
                  break;
                }
                searchStatusIndex--;
              }
            }
          } else {
            // Try to move down in current column
            if (currentPositionInColumn < currentColumnIssues.length - 1) {
              nextIndex =
                currentColumnIssues[currentPositionInColumn + 1].canvas.id;
            } else if (viewType === "list") {
              // Only in list view, look for next column with issues
              let searchStatusIndex = currentStatusIndex + 1;
              while (searchStatusIndex < orderedStatuses.length) {
                const targetStatus = orderedStatuses[searchStatusIndex];
                const targetColumnIssues = sortColumnIssues(
                  issuesToShow.filter((issue) => {
                    const prd = issue.featurePrd;
                    return prd?.status === targetStatus;
                  }),
                );

                if (targetColumnIssues.length > 0) {
                  // Move to first issue in next column with issues
                  nextIndex = targetColumnIssues[0].canvas.id;
                  break;
                }
                searchStatusIndex++;
              }
            }
          }
          break;
        }

        case "ArrowLeft":
        case "ArrowRight": {
          // Get the target status
          let searchStatusIndex = currentStatusIndex;
          const direction = e.key === "ArrowLeft" ? -1 : 1;

          // Find current position in column
          const currentColumnIssues = sortColumnIssues(
            issuesToShow.filter((issue) => {
              const prd = issue.featurePrd;
              return prd?.status === currentStatus;
            }),
          );
          const currentPositionInColumn = currentColumnIssues.findIndex(
            (issue) => issue.canvas.id === currentFocusedId,
          );

          // Keep searching in the direction until we find a column with issues or hit the edge
          while (true) {
            searchStatusIndex += direction;

            // Stop if we've hit the edge
            if (
              searchStatusIndex < 0 ||
              searchStatusIndex >= orderedStatuses.length
            ) {
              break;
            }

            const targetStatus = orderedStatuses[searchStatusIndex];

            // Get all issues in the target column
            const targetColumnIssues = sortColumnIssues(
              issuesToShow.filter((issue) => {
                const prd = issue.featurePrd;
                return prd?.status === targetStatus;
              }),
            );

            // If we found a column with issues
            if (targetColumnIssues.length > 0) {
              if (viewType === "list") {
                // In list view, always focus the first issue in the target column
                nextIndex = targetColumnIssues[0].canvas.id;
              } else {
                // In grid view, try to maintain the same index position
                if (currentPositionInColumn < targetColumnIssues.length) {
                  nextIndex =
                    targetColumnIssues[currentPositionInColumn].canvas.id;
                } else {
                  // If target column is shorter, focus the last issue
                  nextIndex =
                    targetColumnIssues[targetColumnIssues.length - 1].canvas.id;
                }
              }
              break;
            }
            // If this column was empty, the while loop will continue to the next column
          }
          break;
        }
      }

      // Update focus if we found a valid next issue
      if (nextIndex) {
        setFocusedIssue(nextIndex);
      }
    },
    [
      issuesToShow,
      focusedIssueId,
      lastFocusedIssueId,
      sortColumnIssues,
      viewType,
      isLoading,
      selectedIssueIds,
      setFocusedIssue,
      setLastFocusedIssue,
      clearFocus,
    ],
  );

  // Add keyboard event listener
  useEffect(() => {
    // Only add keyboard listener if not loading
    if (!shouldShowLoading) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [handleKeyDown, shouldShowLoading]);

  useEffect(() => {
    if (!scrollToStatus || !containerRef.current) {
      return;
    }

    if (shouldShowLoading) {
      return;
    }

    const SCROLL_DELAY_MS = 100;
    const SCROLL_OPTIONS: ScrollIntoViewOptions = {
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    };

    const scrollTimeout = setTimeout(() => {
      try {
        const targetColumnElement = containerRef.current?.querySelector(
          `[data-status-column="${scrollToStatus}"]`,
        ) as HTMLElement | null;

        if (targetColumnElement) {
          targetColumnElement.scrollIntoView(SCROLL_OPTIONS);
        } else {
          const retryTimeout = setTimeout(() => {
            const retryElement = containerRef.current?.querySelector(
              `[data-status-column="${scrollToStatus}"]`,
            ) as HTMLElement | null;
            if (retryElement) {
              retryElement.scrollIntoView(SCROLL_OPTIONS);
            }
          }, 500);
          return () => clearTimeout(retryTimeout);
        }
      } catch (error) {
        console.warn("Failed to scroll to status column:", error);
      } finally {
        setScrollToStatus(null);
      }
    }, SCROLL_DELAY_MS);

    return () => clearTimeout(scrollTimeout);
  }, [scrollToStatus, shouldShowLoading, setScrollToStatus]);

  const handleGlobalClick = useCallback(
    (e: MouseEvent) => {
      // Don't handle clicks while loading
      if (shouldShowLoading) return;

      // Don't clear focus if clicking inside an issue
      const isClickInsideIssue = Object.values(issueRefs.current).some((ref) =>
        ref.contains(e.target as Node),
      );

      if (!isClickInsideIssue) {
        clearFocus();
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          clearSelection();
        }
      }
    },
    [isLoading, clearSelection],
  );

  // Add global click listener
  useEffect(() => {
    if (!shouldShowLoading) {
      window.addEventListener("click", handleGlobalClick);
      return () => window.removeEventListener("click", handleGlobalClick);
    }
  }, [handleGlobalClick, shouldShowLoading]);

  useEffect(() => {
    return () => {
      issueSelectionOptions.forEach((option) => {
        unregisterShortcut(option.registerKeys);
      });
      hideCategory(OptionCategory.ISSUE_SELECTION);
    };
  }, []);

  // Create refs for the container and to track focused elements
  const containerRef = useRef<HTMLDivElement>(null);
  const issueRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  // Scroll focused issue into view when focus changes
  useEffect(() => {
    if (focusedIssueId && issueRefs.current[focusedIssueId]) {
      issueRefs.current[focusedIssueId].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [focusedIssueId]);

  const handleItemClick = useCallback(
    (canvas: Canvas) => {
      const e = window.event as MouseEvent;

      // Only update focus if not shift-clicking
      if (!e.shiftKey) {
        setLastFocusedIssue(canvas.id);
      }

      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();

        handleToggleSelectionShortcut(canvas.id);
      } else if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();

        if (selectedIssueIds.size === 0) {
          // if there are no selected issues, select the current issue
          handleToggleSelectionShortcut(canvas.id);
          setFocusedIssue(canvas.id);
        } else {
          const lastSelectedIssue = issuesToShow.find(
            (issue) => issue.canvas.id === lastSelectedIssueId,
          );

          if (!lastSelectedIssue) return;

          // Clear only the selection set, preserving lastSelectedIssueId
          clearSelectionOnly();

          const lastSelectedIssueStatus = lastSelectedIssue.featurePrd?.status;
          const lastSelectedIssueInfo = {
            status: lastSelectedIssueStatus,
            columnIndex: Object.values(KanbanDefaultStates).indexOf(
              lastSelectedIssueStatus as KanbanDefaultStates,
            ),
            indexInColumn: sortColumnIssues(
              issuesToShow.filter((issue) => {
                const prd = issue.featurePrd;
                return prd?.status === lastSelectedIssueStatus;
              }),
            ).findIndex((c) => c.canvas.id === lastSelectedIssue.canvas.id),
          };
          const currentIssueStatus = issuesToShow.find(
            (issue) => issue.canvas.id === canvas.id,
          )?.featurePrd?.status;
          const currentIssueInfo = {
            status: currentIssueStatus,
            columnIndex: Object.values(KanbanDefaultStates).indexOf(
              currentIssueStatus as KanbanDefaultStates,
            ),
            indexInColumn: sortColumnIssues(
              issuesToShow.filter((issue) => {
                const prd = issue.featurePrd;
                return prd?.status === currentIssueStatus;
              }),
            ).findIndex((c) => c.canvas.id === canvas.id),
          };

          let selectableIssueIds: string[] = [];

          if (lastSelectedIssueId === canvas.id) {
            // if the last selected issue is the current issue, just select the current issue
            selectableIssueIds = [canvas.id];
          } else if (
            lastSelectedIssueInfo.columnIndex === currentIssueInfo.columnIndex
          ) {
            // the issues are in the same column, select issues in between
            const startIndex = Math.min(
              lastSelectedIssueInfo.indexInColumn,
              currentIssueInfo.indexInColumn,
            );
            const endIndex = Math.max(
              lastSelectedIssueInfo.indexInColumn,
              currentIssueInfo.indexInColumn,
            );

            const columnIssues = sortColumnIssues(
              issuesToShow.filter((issue) => {
                const prd = issue.featurePrd;
                return prd?.status === lastSelectedIssueStatus;
              }),
            );
            const slicedColumnIssues = columnIssues.slice(
              startIndex,
              endIndex + 1,
            );
            selectableIssueIds = slicedColumnIssues.map((c) => c.canvas.id);
          } else {
            // the issues are in different columns, select a range of issues
            const lowerIssueInfo =
              lastSelectedIssueInfo.columnIndex < currentIssueInfo.columnIndex
                ? lastSelectedIssueInfo
                : currentIssueInfo;
            const higherIssueInfo =
              lastSelectedIssueInfo.columnIndex < currentIssueInfo.columnIndex
                ? currentIssueInfo
                : lastSelectedIssueInfo;

            const kanbanStates = Object.values(KanbanDefaultStates);
            for (
              let i = lowerIssueInfo.columnIndex;
              i <= higherIssueInfo.columnIndex;
              i++
            ) {
              const columnIssues = sortColumnIssues(
                issuesToShow.filter((issue) => {
                  const prd = issue.featurePrd;
                  return prd?.status === kanbanStates[i];
                }),
              );

              let issuesToAdd: any[] = [];
              if (i === lowerIssueInfo.columnIndex) {
                // add the lower column issue, and issues after it
                issuesToAdd = columnIssues.slice(
                  lowerIssueInfo.indexInColumn,
                  columnIssues.length,
                );
              } else if (i === higherIssueInfo.columnIndex) {
                // add the higher column issue, and issues before it
                issuesToAdd = columnIssues.slice(
                  0,
                  higherIssueInfo.indexInColumn + 1,
                );
              } else {
                // add all issues in the column
                issuesToAdd = columnIssues;
              }
              selectableIssueIds.push(...issuesToAdd.map((c) => c.canvas.id));
            }
          }
          selectAllVisible(selectableIssueIds);
        }
      } else {
        onSelect(canvas);
      }
    },
    [
      onSelect,
      project,
      handleToggleSelectionShortcut,
      clearSelection,
      selectedIssueIds,
      lastSelectedIssueId,
      issuesToShow,
      sortColumnIssues,
    ],
  );

  // Add this near other memoized values, before the return statement
  // Memoize the mapped users array
  const mappedUsers = useMemo(() => {
    if (!Array.isArray(project?.users)) return [];
    return (project.users as any[]).map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      avatarUrl: u.avatarUrl,
    }));
  }, [project?.users]);

  // Memoize the mapped labels array
  const mappedLabels = useMemo(() => {
    if (!Array.isArray(project?.labels)) return [];
    return (project.labels as any[]).map((l) => ({
      id: l.id,
      label: l.label,
      name: l.name,
      color: l.color,
    }));
  }, [project?.labels]);

  const isFiltersApplied = React.useMemo(() => {
    return Boolean(
      (filters?.userIds?.length && filters?.userIds?.length > 0) ||
        (filters?.labels?.length && filters?.labels?.length > 0) ||
        (filters?.statuses?.length && filters?.statuses?.length > 0) ||
        (filters?.dueDates &&
          Object.values(filters?.dueDates).some((value) => value)) ||
        (filters?.estimates?.length && filters?.estimates?.length > 0) ||
        (filters?.priorities?.length && filters?.priorities?.length > 0),
    );
  }, [filters]);

  function renderUI() {
    if (
      currentProjectIssues &&
      Object.keys(currentProjectIssues).length === 0
    ) {
      return <NoIssues handleButtonClick={handleButtonClick} />;
    }

    if (
      filters &&
      project &&
      issuesToShow.length === 0 &&
      currentProjectIssues &&
      Object.keys(currentProjectIssues).length > 0
    ) {
      return (
        <NoResults
          isFiltersApplied={isFiltersApplied}
          isSearchApplied={filters.search !== ""}
          hiddenCanvases={Object.keys(currentProjectIssues).length}
          clearFilters={clearFilters}
        />
      );
    }

    return (
      <ContextMenu>
        <ContextMenuTrigger
          onContextMenu={(e) => {
            const shouldOpen =
              useContextMenuStore.getState().shouldOpenContextMenu;
            if (!shouldOpen) {
              e.preventDefault();
            }
            useProjectModalStore
              .getState()
              .addProjectModal("issue-context-menu");
            // Reset the flag for next time
            useContextMenuStore.getState().setShouldOpenContextMenu(false);
          }}
        >
          {viewType === "list" ? (
            <DragDropContext
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              enableDefaultSensors={true}
            >
              <div
                className="h-[calc(100vh-120px)] overflow-y-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgb(82 82 91) transparent",
                }}
              >
                {shouldShowLoading ? (
                  <div className="flex flex-col divide-y bg-secondary-n-1000">
                    {new Array(3).fill(0).map((_, i) => (
                      <div key={i} className="flex flex-col shrink-0">
                        {/* Status Header Skeleton */}
                        <div className="flex items-center justify-between pr-9 pl-11 py-2.5 bg-secondary-n-950 sticky top-0">
                          <div className="flex items-center gap-1">
                            <div className="h-4 w-4 bg-secondary-n-800 rounded animate-pulse" />
                            <div className="h-4 w-20 bg-secondary-n-800 rounded animate-pulse" />
                            <div className="h-4 w-8 bg-secondary-n-800 rounded animate-pulse" />
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="h-7 w-7 bg-secondary-n-800 rounded animate-pulse" />
                          </div>
                        </div>
                        <div className="flex flex-col">
                          {new Array(2).fill(0).map((_, j) => (
                            <div
                              key={j}
                              className="p-4 border-b border-secondary-n-900"
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-secondary-n-800 rounded animate-pulse" />
                                <div className="flex-1 space-y-2">
                                  <div className="h-4 w-3/4 bg-secondary-n-800 rounded animate-pulse" />
                                  <div className="h-3 w-1/2 bg-secondary-n-800 rounded animate-pulse" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col divide-y bg-secondary-n-1000">
                    {(() => {
                      const availableStatusNames = allProjectStatuses.map(
                        (statusItem) => statusItem.status,
                      );
                      return allProjectStatuses.map((statusItem) => {
                        const status = statusItem.status;

                        const StatusIcon = statusIconMap.get(status);
                        const StatusHeaderIcon = StatusIcon;

                        // Get and sort issues for this status
                        const statusIssues = sortColumnIssues(
                          issuesToShow.filter((issue) => {
                            const resolvedStatus = resolveIssueStatus(
                              issue.featurePrd?.status,
                              availableStatusNames,
                            );
                            return resolvedStatus === status;
                          }),
                        );

                        return (
                          <div
                            key={statusItem.key}
                            data-status-column={status}
                            className="flex flex-col shrink-0"
                          >
                            {/* Status Header */}
                            <div className="flex items-center justify-between pr-9 pl-11 py-2.5 bg-secondary-n-950 sticky top-0 z-10">
                              <div className="flex items-center gap-1">
                                {StatusHeaderIcon && <StatusHeaderIcon />}
                                <span className="caption text-2xs font-medium min-w-0">
                                  <span
                                    className="truncate max-w-[120px]"
                                    title={status}
                                  >
                                    {status}
                                  </span>
                                </span>
                                <span className="px-1 rounded-xs text-2xs font-medium bg-secondary-n-900 text-center flex items-center justify-center">
                                  {statusIssues.length}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() =>
                                    handleButtonClick(statusItem.key)
                                  }
                                  className="flex items-center justify-center w-7 h-7 rounded-xs hover:bg-white/10 transition-colors text-lg text-secondary-n-600"
                                  aria-label="Add Feature"
                                  type="button"
                                >
                                  <PlusIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {/* Status Items */}
                            <Droppable
                              droppableId={status}
                              ignoreContainerClipping={true}
                            >
                              {(provided) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className={cn("flex flex-col")}
                                >
                                  {statusIssues.map((statusIssue, index) => {
                                    return (
                                      <DraggableIssueCard
                                        statusIssue={statusIssue}
                                        project={project as Project}
                                        statuses={comprehensiveStatusList}
                                        index={index}
                                        onSelect={handleItemClick}
                                        icon={issueStatusIconMap.get(
                                          statusIssue.canvas.id,
                                        )}
                                        issueRefs={issueRefs.current}
                                        visibleIssueIds={issuesToShow.map(
                                          (c) => c.canvas.id,
                                        )}
                                        isDragging={isDragging}
                                      />
                                    );
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </DragDropContext>
          ) : (
            <DragDropContext
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              enableDefaultSensors={true}
            >
              <div className="flex flex-row min-h-[calc(100vh-120px)] relative px-1 overflow-x-auto horizontal-scrollbar">
                {allProjectStatuses.map((statusItem) => {
                  const status = statusItem.status;
                  const colState = stateToIssues[status];

                  if (!colState) {
                    return null;
                  }

                  return (
                    <KanbanColumn
                      key={statusItem.key}
                      stateKey={colState.stateKey}
                      title={colState.title}
                      issues={colState.issues}
                      project={project}
                      onSelect={handleItemClick}
                      onClickAdd={() => handleButtonClick(statusItem.key)}
                      issueRefs={issueRefs.current}
                      visibleIssueIds={issuesToShow.map((c) => c.canvas.id)}
                      comprehensiveStatusList={comprehensiveStatusList}
                      customProjectStatuses={customProjectStatuses}
                      isLoading={shouldShowLoading}
                      isDragging={isDragging}
                      statusIconMap={statusIconMap}
                      issueStatusIconMap={issueStatusIconMap}
                    />
                  );
                })}
              </div>
            </DragDropContext>
          )}
        </ContextMenuTrigger>
        <IssueContextMenu
          users={mappedUsers}
          labels={mappedLabels}
          status={comprehensiveStatusList}
        />
      </ContextMenu>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col h-full overflow-x-auto overflow-y-hidden select-none"
      ref={containerRef}
    >
      {renderUI()}

      <CreateCanvasModal
        openCanvasState={getOpenCanvasState()}
        setOpenCanvasState={(state: string | null) => {
          setIsOpen(null);
          useProjectModalStore
            .getState()
            .removeProjectModal("create-canvas-modal");
        }}
        project={currentProject}
        createIndex={
          typeof isOpen === "string"
            ? (() => {
                // Handle default status keys
                if (
                  KanbanDefaultStates[
                    isOpen as keyof typeof KanbanDefaultStates
                  ]
                ) {
                  const statusName =
                    KanbanDefaultStates[
                      isOpen as keyof typeof KanbanDefaultStates
                    ];
                  return stateToIssues?.[statusName]?.issues?.length || 0;
                }

                // Handle custom status IDs
                if (Array.isArray(project?.statuses)) {
                  const customStatus = project.statuses?.find(
                    (status) => status?.id === isOpen,
                  );
                  if (customStatus?.name) {
                    return (
                      stateToIssues?.[customStatus.name]?.issues?.length || 0
                    );
                  }
                }

                // Fallback
                return 0;
              })()
            : 0
        }
        filters={filters}
      />

      <NoRepositoryModal
        isOpen={showNoRepoModal}
        onClose={() => setShowNoRepoModal(false)}
        onCreateRepository={handleCreateRepository}
      />

      {showCreateRepoModal && issuesToShow && issuesToShow.length > 0 && (
        <CreateRepositoryModal
          canvasId={issuesToShow[0].canvas.id}
          isOpen={showCreateRepoModal}
          onClose={() => {
            setShowCreateRepoModal(false);
          }}
          onRepoCreated={() => {
            setShowCreateRepoModal(false);
          }}
          initialSelectedOrg={null}
          originatingProvider={originatingGitProvider}
        />
      )}
    </div>
  );
};

export default React.memo(CanvasKanban);
